import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@space/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that must NOT get silently lost ───────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against PowerSync's **view** can
 * match **ZERO rows and report "success"**. Measured in this very repo (the
 * local-stack `activity` table, course `06a131d0`): the course gets created, 1.03s
 * later the unit gets created and **in between there is NO
 * `course/updated` op at all** — the title the author had just written never made it
 * into a CRUD operation. In a run where everything got queued before
 * the first upload (`136ef638`) it IS there, 7ms after the `created`.
 *
 * The mechanism: once the CRUD queue drains, PowerSync **rebuilds the
 * local view from the last checkpoint**. If the checkpoint doesn't carry
 * the just-created row yet, the row is **ABSENT during that instant** — and an
 * UPDATE against it writes nothing, generates no op, and **does not fail**.
 *
 * Why the absence of an error proves nothing here: the server's write-path
 * **always deliberately returns 2xx** (a 4xx would block the client's queue
 * forever). That case is a DIFFERENT path — there the server rejects and
 * stays silent — but the criterion is the same: a user write that
 * disappears without a trace is the worst failure mode.
 *
 * This module's contract: write, **RE-READ**, compare. If the value
 * didn't land, retry with backoff; if it's still not landed once the budget is exhausted,
 * **THROW** — the caller surfaces it instead of leaving the user staring at a
 * field that doesn't represent anything actually saved.
 *
 * Converged with the `listto-app` canon (names and surface: `hasUpdatedAt`
 * instead of the `stampUpdatedAt` this module was born with, `scope` and
 * `verifyBy` brought over intact) — two names for the same concept is
 * exactly how expensive divergences are born. Convergence 2026-08-13,
 * ADDITIVE: the 52 existing calls (`patchRowVerified`/`waitForRowPresent`
 * in `packages/shared/src/**\/mutations.ts` and `users/mutations.ts`) keep
 * compiling and passing untouched — `hasUpdatedAt` has the SAME semantics
 * as `stampUpdatedAt` (same default `true`, same effect when set to
 * `false`), only the name changes. `scope` and `verifyBy` are opt-in: no
 * existing caller uses them yet. Not blindly copied — this repo has no
 * compound mutations inside a transaction (there is no
 * `db.transaction(...)` in `packages/shared`), so `waitForRowPresent` has
 * no caller of its own today, but it's included anyway: it's half of the
 * canon's contract and `saveLessonProgressMutation` (existing SELECT →
 * manual UPDATE or INSERT, see `courses/mutations.ts`) is exactly the type
 * of compound operation it exists for.
 *
 * A BEHAVIOR difference, not just a naming one, was fixed while converging
 * (it wasn't left diverging): the previous version of this module did NOT ignore
 * the `updated_at` the `patch` itself might carry — it let it slip into the
 * comparison as-is. If some caller reused a `values` shared
 * between its INSERT branch and its UPDATE branch (the `agape-church-app`
 * pattern that motivated the rule in the canon), the imported `patch.updated_at`
 * would end up compared against the FRESH `updated_at` this module stamps on
 * every attempt — which never matches — and the write would ALWAYS fail for the wrong
 * reason. No caller in this repo does that today (audited: no
 * mutation in `packages/shared/src/**\/mutations.ts` passes `updated_at` inside
 * the `patch`), so the change doesn't alter the observable behavior of
 * the 52 existing calls — but closing it now prevents the first
 * caller that does do it from tripping over a silent failure at its root. It
 * keeps the canon's semantics: it is ALWAYS ignored, not only when it bites.
 *
 * ── `scope`: tenant isolation, not just correctness ───────────────────────────
 * Brought over from `comandero-app` via the canon, where dropping the
 * `organization_id` condition from the `WHERE` when migrating to `patchRowVerified` opened up
 * cross-organization writes. It applies the same here: the academy is
 * multi-organization (cohorts, enrollments, classrooms) and `infra/sync_streams.yaml`
 * syncs ALL the organizations/cohorts the user is a
 * member of — `my_org_ids`, `my_cohort_ids`, `my_teaching_cohort_ids`,
 * `my_admin_org_ids`, `my_admin_campus_ids` — not just the active one. `scope`
 * restores that filter **applied to both the UPDATE and the re-read**: a
 * row that doesn't satisfy the scope is treated the same as an absent row.
 *
 * Audited (2026-08-13) whether any of the 52 existing calls LOST an
 * organization/cohort condition when converted to `patchRowVerified`
 * (`f92740b1`/`16bc571f`): NO — every `db.update(...).where(...)` was compared
 * BEFORE the conversion (`git show f92740b1 -- packages/shared/src`) against
 * the form afterward, and none had `organization_id`/`campus_id`/
 * `cohort_id` in the `WHERE` EITHER BEFORE OR AFTER: all of them already used
 * plain `eq(table.id, id)` from before this module even existed. The
 * conversion did not introduce the hole. It's still a LATENT hole — just like
 * in `comandero` before the incident: no mutation in this repo today passes
 * `organizationId`/`cohortId` as a parameter that could be turned into
 * `scope` without extra plumbing through `apps/web` (with the exception of
 * the Class 2 upserts in `academy/assessment/mutations.ts`, which already receive
 * `organizationId` but don't use it as `scope` because they don't go through
 * `patchRowVerified`). Closing that plumbing in the ~45 domain mutations is
 * a separate campaign, not this surgical change: `scope` stays available and
 * tested (see `__tests__/verified-write.test.ts`) so whoever tackles it
 * inherits it instead of reinventing it. Signal that would reopen it: a
 * measured (not just hypothetical) write landing in the wrong organization.
 *
 * ── Closing the plumbing (2026-08-13) — and the aula-courses-app correction ──
 * Of the ~58 calls to patchRowVerified/waitForRowPresent, ~30 touch a
 * table with an organization_id/cohort_id/campus_id column. Of those, scope
 * was wired for ~20 and ~10 were left WITHOUT scope, documented in place — each
 * with its exact reason, not a hole by omission:
 *
 *   1. The organization is not always the real authorization boundary.
 *      A lesson from aula-courses-app on this exact assignment: measuring that the
 *      WHERE doesn't scope by organization is measuring a SHAPE, not a defect —
 *      you have to look at the WHERE the SERVER enforces (db/sync.rs, can_*
 *      capabilities), not the client's shape. Here: forum/forum_thread/forum_post/
 *      resource/assignment (course content) are authorized by course
 *      AUTHOR/EDITOR — a user boundary, not an organization one — just like
 *      in aula. Adding scope there would protect nothing.
 *   2. The INSERT doesn't always populate the column. The same forum/resource/
 *      assignment get created from authoring (course-editor.tsx) without
 *      organizationId — a course is authored independent of organization and
 *      gets shared across academies via cohorts. Confirmed by reading the
 *      insert(...), not hypothetical: with scope by the active org,
 *      eq(col, orgId) would NEVER have matched against that NULL column, and EVERY
 *      edit would have thrown LocalWriteLostError — the "this would break a
 *      legitimate case" signal that POWERSYNC_VERIFIED_WRITE.md calls for,
 *      caught by reading the code before a test caught it by accident.
 *   3. Some views aggregate SEVERAL organizations/cohorts at once, so
 *      there's no single scope value to pass: tutor-panel.tsx's review
 *      queue (submission_version, my_cohorts_teacher +
 *      org_admin_scope), the notification bell (notification, all
 *      of the user's orgs), and the global trash (trash-view.tsx, every
 *      kind from every org). Using the row's OWN field as scope
 *      there would be circular (verifying the row against data read from
 *      itself) — zero real protection.
 *   4. cohort_id was the correct scope, not organization_id, in the classroom
 *      chat (cohort_chat_message, authorized by "ACTIVE enrollment in THIS
 *      cohort") and attendance (attendance_record, "the cohort's
 *      teacher") — the organization is a looser boundary than the real one, and in
 *      both cases cohort_id was already available without extra plumbing.
 *
 * The ~20 cases that ARE wired share an organization boundary
 * verified in the write-path's own comment: ManageAcademics/
 * org_rbac (owner/org_admin/campus_admin) — cohort, program,
 * cohort_enrollment, campus, room, grade_category, schedule_slot,
 * session_module, announcement. Case-by-case detail in each mutation.
 * Signal that would reopen any of the exclusions: a MEASURED (not
 * just hypothetical) write landing in the wrong organization/cohort through that path.
 *
 * ── THREE pieces about `updated_at`, and they are THREE different things ─────
 *   1. **Stamping** (`hasUpdatedAt`, default `true`): whether this helper
 *      writes `updated_at` in the `SET` of every attempt. `false` for tables
 *      without that column (e.g. `cohort_chat_reaction`, `notification`) — with
 *      the default at `true` the `UPDATE` would reference a nonexistent column
 *      and blow up at runtime (it bit in `vigia-fleet-app`).
 *   2. **Ignoring the `updated_at` the `patch` might carry** (always, no flag).
 *   3. **Verifying BY `updated_at`** (`verifyBy: "updated_at"`, opt-in,
 *      NEVER automatic): for patches that cannot be compared by value
 *      (an atomic-increment SQL expression, e.g.). Requires
 *      `hasUpdatedAt` to be other than `false` — combining it with `hasUpdatedAt:
 *      false` is a configuration error and throws immediately, before
 *      writing anything.
 */

/**
 * Retry backoff. Sums to ≈ 6.05s: comfortably covers a checkpoint's
 * round trip (measured at ~1s against the local stack) without leaving a
 * quick interaction hanging forever if the row truly doesn't exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Extra equality condition — defense-in-depth tenant isolation. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests don't take seconds. */
  backoffMs?: readonly number[];
  /**
   * Extra conditions the row must ALSO satisfy, besides `id` —
   * typically `organization_id`. Applied to both the UPDATE and the
   * re-read: a row that doesn't satisfy the scope is treated the same as an
   * absent row (see the security note above).
   */
  scope?: readonly ScopeCondition[];
  /**
   * Whether the table has an `updated_at` column. Defaults to `true` (the
   * usual behavior: stamped on every attempt). Set it to `false`
   * on tables that do NOT have that column — with `false`, `patchRowVerified`
   * never puts it in the `SET`, and `verifyBy: "updated_at"` stops being valid.
   */
  hasUpdatedAt?: boolean;
  /**
   * How to verify the write landed, when the patch cannot be
   * compared by value (e.g. an atomic-increment SQL expression).
   * EXPLICIT mode, never automatic: `"updated_at"` compares the stamp
   * this helper stamps on every attempt — it proves THIS write landed,
   * not that the final value is the expected one. Requires `hasUpdatedAt`
   * to be other than `false`. If omitted and the patch carries a SQL expression,
   * the by-value comparison can never match and the function ends up throwing
   * `LocalWriteLostError` even though the write did land: a closed failure,
   * not a false green — an automatic fallback (comparing `updated_at` when
   * no literal field remains) is deliberately ruled out for the same reason.
   */
  verifyBy?: "updated_at";
}

/** Error for a local-first write that never landed in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/**
 * Adds `scope` conditions to already-existing `options` without stepping on
 * what the caller already brought (e.g. `backoffMs` in a test) — avoids each
 * of the ~30 mutations closing the isolation hole having to repeat the same
 * `{ ...options, scope: [...] }` by hand. Conditions with an `undefined`
 * `column` are DISCARDED (table without that column, e.g. `lesson` in the trash
 * cascade) as is an `undefined`/`null` `value` (not `eq(col, undefined)`: a caller
 * that doesn't yet know the active organizationId/cohortId must not fabricate a
 * condition that would never match) — "no scope" (today's same
 * behavior) is preferred over a false `LocalWriteLostError`.
 */
export function withScope(
  options: VerifiedWriteOptions | undefined,
  extra: readonly ({ column: SQLiteColumn | undefined; value: unknown } | undefined)[],
): VerifiedWriteOptions {
  const defined = extra.filter(
    (c): c is ScopeCondition =>
      c !== undefined && c.column !== undefined && c.value !== undefined && c.value !== null,
  );
  if (defined.length === 0) {
    return options ?? {};
  }
  return { ...options, scope: [...(options?.scope ?? []), ...defined] };
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` without scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads the row by id (+ `scope`, if given), or `undefined` if it's not in
 * the local view or doesn't satisfy the scope — the same result as "row
 * absent", on purpose (see the security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read is
  // by primary key (+ scope), so the row is a plain record of
  // columns.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- generic over SQLiteTable, the real shape is only known at runtime
  const rows = (await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, re-reads, compares the patch's
 * fields, and retries while they haven't landed. Throws `LocalWriteLostError`
 * if they're still not there once the backoff is exhausted.
 *
 * If `options.hasUpdatedAt` is not `false` (the normal case), this function
 * sets `updated_at` on every attempt — any `updated_at` the `patch` itself
 * carries is ALWAYS ignored, both when writing and when comparing (see the
 * note above); the rest of the `patch` must carry only domain fields. With
 * `hasUpdatedAt: false` the `SET` doesn't touch it at all. With
 * `options.verifyBy === "updated_at"` the patch is not compared by value: it
 * checks that THIS attempt's stamp landed — combining it with
 * `hasUpdatedAt: false` is a configuration error and throws immediately,
 * before writing anything.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];
  const hasUpdatedAt = options.hasUpdatedAt ?? true;
  if (options.verifyBy === "updated_at" && !hasUpdatedAt) {
    throw new Error(
      'verifyBy: "updated_at" is incompatible with hasUpdatedAt: false — the table does not have that column to compare.',
    );
  }
  const { updated_at: _ignoredUpdatedAt, ...domainPatch } = patch;

  for (let attempt = 0; ; attempt++) {
    const stampedAt = hasUpdatedAt ? nowISO() : undefined;
    await db
      .update(table)
      .set(hasUpdatedAt ? { ...domainPatch, updated_at: stampedAt } : domainPatch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    const landed =
      row !== undefined &&
      (options.verifyBy === "updated_at"
        ? sameValue(row.updated_at, stampedAt)
        : Object.entries(domainPatch).every(([key, value]) => sameValue(row[key], value)));
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(domainPatch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for the row to EXIST in the local view before touching it (+ `scope`,
 * if given, with the same isolation criteria as `patchRowVerified`).
 *
 * For mutations that aren't a simple patch — a compound operation where it
 * isn't worth retrying inside the transaction (it would sleep while holding
 * the write lock), and where operating on an absent row is worse than losing
 * a field: an UPDATE that matches nothing while the sibling INSERT does
 * happen leaves orphaned or duplicated data.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}
